import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, GoogleAuthProvider } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const configured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);
export const firebaseApp = configured ? initializeApp(firebaseConfig) : null;
export const auth = firebaseApp ? getAuth(firebaseApp) : null;
export const db = firebaseApp ? getFirestore(firebaseApp) : null;
// us-central1 explicitly, matching the region every Cloud Function in
// functions/index.js actually deploys to — the SDK default happens to agree
// today, but a function callable from the wrong region fails with an opaque
// CORS-shaped error rather than a clear one, so this isn't left implicit.
export const functionsClient = firebaseApp ? getFunctions(firebaseApp, "us-central1") : null;
export const googleProvider = new GoogleAuthProvider();

// `npm run dev` targets a local Firebase Emulator Suite by default (Auth +
// Firestore) instead of live production — see `npm run dev:prod` to opt
// back into the real towardsgm project for one run, or set
// VITE_FIREBASE_EMULATOR=false in .env to make that the default. Native
// (Capacitor) Google sign-in goes through @capacitor-firebase/authentication
// straight to the platform SDK, which does NOT honor this — mobile builds
// always talk to production regardless of this flag (see MOBILE.md).
//
// import.meta.env.DEV is inlined at build time, so this whole branch (and
// the emulator host strings) is dead-code-eliminated from production
// builds no matter how the env var is set.
export const usingEmulator = import.meta.env.DEV && import.meta.env.VITE_FIREBASE_EMULATOR !== "false";
if (usingEmulator && auth && db) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  // `npm run dev` doesn't start the Functions emulator (see package.json) —
  // callable functions just get a connection-refused in that default setup,
  // which is the safe failure: an ID token minted by the local Auth emulator
  // is not valid against deployed prod functions, so pointing this at 5001
  // ensures a stray call fails locally rather than silently hitting prod.
  // Run `firebase emulators:start --only auth,firestore,functions` (with
  // functions/.secret.local populated, see docs/PAYMENTS.md) to actually
  // exercise checkout/webhook locally.
  if (functionsClient) connectFunctionsEmulator(functionsClient, "127.0.0.1", 5001);
}
let analytics = null;
let analyticsMod = null;
let analyticsInitPromise = null;
const ANALYTICS_CONSENT_KEY = "towardsgm:analytics-consent";

export function analyticsConsent() {
  try {
    return localStorage.getItem(ANALYTICS_CONSENT_KEY) || "unset";
  } catch {
    return "unset";
  }
}

// The Analytics SDK (and the gtag.js it fetches) is only pulled in once
// consent is actually granted, so most visitors never download it at all.
// Non-blocking and best-effort: analytics must never gate the app.
function initAnalytics() {
  if (analyticsInitPromise) return analyticsInitPromise;
  if (!firebaseApp || !firebaseConfig.measurementId || typeof window === "undefined") {
    analyticsInitPromise = Promise.resolve();
    return analyticsInitPromise;
  }
  analyticsInitPromise = import("firebase/analytics")
    .then(async (mod) => {
      if (!(await mod.isSupported())) return;
      analyticsMod = mod;
      analytics = mod.getAnalytics(firebaseApp);
      mod.setAnalyticsCollectionEnabled(analytics, true);
    })
    .catch(() => {});
  return analyticsInitPromise;
}

export function setAnalyticsConsent(allowed) {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, allowed ? "granted" : "denied");
  } catch {
    // Collection state still applies for this page even without local storage.
  }
  if (allowed) initAnalytics();
  else if (analytics) analyticsMod.setAnalyticsCollectionEnabled(analytics, false);
}

if (analyticsConsent() === "granted") initAnalytics();

// Keep analytics payloads aggregate-only: never send usernames, email
// addresses, game moves, imported PGN, feedback text, or Firebase UIDs.
export function trackEvent(name, params = {}) {
  if (!analytics) return;
  try {
    analyticsMod.logEvent(analytics, name, params);
  } catch {
    // Measurement must never interrupt a chess session.
  }
}
